/**
 * The languages Upweo speaks.
 *
 * Adding one is a two-step change: an entry here and a catalogue file. Nothing
 * else in the codebase enumerates locales, so a new language cannot be half
 * wired up — the type of `Catalogue` forces the new file to be complete.
 */
export const LOCALES = {
    en: { code: 'en', englishName: 'English', nativeName: 'English', dir: 'ltr' },
    es: { code: 'es', englishName: 'Spanish', nativeName: 'Español', dir: 'ltr' },
    fr: { code: 'fr', englishName: 'French', nativeName: 'Français', dir: 'ltr' },
    de: { code: 'de', englishName: 'German', nativeName: 'Deutsch', dir: 'ltr' },
    hi: { code: 'hi', englishName: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr' },
    ar: { code: 'ar', englishName: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
};
export const LOCALE_CODES = Object.keys(LOCALES);
export const DEFAULT_LOCALE = 'en';
export const isLocale = (value) => Object.prototype.hasOwnProperty.call(LOCALES, value);
export const localeMeta = (locale) => LOCALES[locale];
export const isRtl = (locale) => LOCALES[locale].dir === 'rtl';
/**
 * Pick the best language for a visitor.
 *
 * Parses an `Accept-Language` header, honouring q-weights, and matches on the
 * PRIMARY SUBTAG. A browser asking for `pt-BR` should get Portuguese if we ever
 * ship it, and a browser asking for `en-GB` must not fall through to the
 * default just because the region differs — region-exact matching is the most
 * common way locale negotiation quietly fails.
 *
 * `allowed` lets a store restrict itself to the languages it has actually
 * translated its products into, rather than offering all six and showing a
 * half-translated shop.
 */
export const negotiateLocale = (acceptLanguage, allowed = LOCALE_CODES, fallback = DEFAULT_LOCALE) => {
    const permitted = allowed.length > 0 ? allowed : LOCALE_CODES;
    if (acceptLanguage) {
        const ranked = acceptLanguage
            .split(',')
            .map((part) => {
            const [tag = '', ...params] = part.trim().split(';');
            const q = params.map((p) => /^\s*q=([\d.]+)\s*$/.exec(p)).find((m) => m !== null)?.[1];
            return { tag: tag.trim().toLowerCase(), q: q === undefined ? 1 : Number(q) };
        })
            // A q of 0 means "explicitly not this one", so it must be dropped rather
            // than merely sorted last.
            .filter((entry) => entry.tag !== '' && entry.q > 0)
            .sort((a, b) => b.q - a.q);
        for (const { tag } of ranked) {
            if (tag === '*') {
                break;
            }
            const primary = tag.split('-')[0] ?? '';
            if (isLocale(primary) && permitted.includes(primary)) {
                return primary;
            }
        }
    }
    // Fall back to the store's own default if it is one it actually offers.
    return permitted.includes(fallback) ? fallback : (permitted[0] ?? DEFAULT_LOCALE);
};
//# sourceMappingURL=locales.js.map